import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, ExternalLink } from "lucide-react";

const PART_STATUSES = ["invited", "applied", "approved", "active", "suspended", "completed"];

export default function Candidates() {
  const [cands, setCands] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/admin/candidates").then((r) => setCands(r.data));
    adminApi.get("/admin/offices").then((r) => setOffices(r.data));
  };
  useEffect(load, []);

  const openEdit = (c) => {
    setSelected(c);
    setForm({
      public_name: c.public_name || "", legal_name: c.legal_name || "", bio: c.bio || "",
      why_running: c.why_running || "", contact_email: c.contact_email || "", phone: c.phone || "",
      paper_ballot_position: c.paper_ballot_position || "not_stated",
      paper_ballot_statement: c.paper_ballot_statement || "",
      participation_status: c.participation_status, published: !!c.published,
      website: c.links?.website || "", facebook: c.links?.facebook || "", x: c.links?.x || "",
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        public_name: form.public_name, legal_name: form.legal_name || null, bio: form.bio || null,
        why_running: form.why_running || null, contact_email: form.contact_email || null, phone: form.phone || null,
        paper_ballot_position: form.paper_ballot_position,
        paper_ballot_statement: form.paper_ballot_statement || null,
        participation_status: form.participation_status, published: form.published,
        links: { website: form.website || null, facebook: form.facebook || null, x: form.x || null },
      };
      await adminApi.patch(`/admin/candidates/${selected.id}`, payload);
      toast.success("Candidate updated");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/candidates", {
        office_id: form.office_id, public_name: form.public_name,
        participation_status: "approved", published: false,
      });
      toast.success("Candidate created (unpublished)");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await adminApi.post(`/admin/candidates/${selected.id}/photo?kind=${kind}`, fd);
      toast.success(`${kind} uploaded`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-candidates-title">Candidates</h1>
        <Button onClick={() => { setForm({ office_id: "", public_name: "" }); setCreating(true); }} data-testid="candidate-create-button">
          <Plus className="h-4 w-4 mr-2" /> New candidate
        </Button>
      </div>

      <Card><CardContent className="p-0">
        <Table data-testid="candidates-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Office</TableHead>
              <TableHead>Election</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Published</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cands.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => openEdit(c)} data-testid={`candidate-row-${c.slug}`}>
                <TableCell className="font-medium">{c.public_name}<p className="text-xs text-muted-foreground font-mono">{c.slug}</p></TableCell>
                <TableCell className="text-sm">{c.office_label}</TableCell>
                <TableCell className="text-sm">{c.election_label}</TableCell>
                <TableCell><Badge variant="outline" className={c.participation_status === "active" || c.participation_status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}>{c.participation_status}</Badge></TableCell>
                <TableCell>{c.published ? <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">published</Badge> : <Badge variant="outline">hidden</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">New candidate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Office</Label>
              <Select value={form.office_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, office_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="candidate-create-office"><SelectValue placeholder="Select office" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Public name</Label>
              <Input className="mt-1.5" value={form.public_name || ""} onChange={(e) => setForm((f) => ({ ...f, public_name: e.target.value }))} data-testid="candidate-create-name" />
            </div>
            <Button onClick={create} disabled={busy || !form.office_id || !(form.public_name || "").trim()} data-testid="candidate-create-submit">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* edit dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{selected.public_name}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-4">
                {selected.photo ? (
                  <img src={assetUrl(selected.photo)} alt="" className="h-16 w-16 rounded-lg object-cover border" />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-slate-100 border flex items-center justify-center text-slate-400 text-xs">No photo</div>
                )}
                <label className="cursor-pointer">
                  <span className="inline-flex items-center text-xs font-medium text-primary hover:underline"><Upload className="h-3.5 w-3.5 mr-1" /> Upload photo</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0], "photo")} data-testid="candidate-photo-input" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Public name</Label><Input className="mt-1.5" value={form.public_name} onChange={(e) => setForm((f) => ({ ...f, public_name: e.target.value }))} data-testid="candidate-edit-name" /></div>
                <div><Label>Legal name</Label><Input className="mt-1.5" value={form.legal_name} onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))} /></div>
                <div><Label>Contact email</Label><Input className="mt-1.5" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} /></div>
                <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              </div>
              <div><Label>Bio</Label><Textarea className="mt-1.5" rows={3} value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} data-testid="candidate-edit-bio" /></div>
              <div><Label>Why running</Label><Textarea className="mt-1.5" rows={3} value={form.why_running} onChange={(e) => setForm((f) => ({ ...f, why_running: e.target.value }))} /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>Website</Label><Input className="mt-1.5" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} /></div>
                <div><Label>Facebook</Label><Input className="mt-1.5" value={form.facebook} onChange={(e) => setForm((f) => ({ ...f, facebook: e.target.value }))} /></div>
                <div><Label>X</Label><Input className="mt-1.5" value={form.x} onChange={(e) => setForm((f) => ({ ...f, x: e.target.value }))} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Paper-ballot position</Label>
                  <Select value={form.paper_ballot_position} onValueChange={(v) => setForm((f) => ({ ...f, paper_ballot_position: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="supports">Supports</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                      <SelectItem value="not_stated">Not stated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Participation status</Label>
                  <Select value={form.participation_status} onValueChange={(v) => setForm((f) => ({ ...f, participation_status: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="candidate-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {PART_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Paper-ballot statement</Label><Textarea className="mt-1.5" rows={2} maxLength={1000} value={form.paper_ballot_statement} onChange={(e) => setForm((f) => ({ ...f, paper_ballot_statement: e.target.value }))} /></div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3.5">
                <div>
                  <p className="text-sm font-medium">Published</p>
                  <p className="text-xs text-muted-foreground">Visible in the public directory and profile pages</p>
                </div>
                <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="candidate-edit-published" />
              </div>
              <div className="flex justify-between items-center">
                <Button onClick={save} disabled={busy} data-testid="candidate-edit-save">Save changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
